// Geometry for the data-overlay ring and tag (TGCLOUD-1670). Pure and
// test-pinned, in the manner of linkedRecordBadge.js, so the clearances can be
// asserted at the shipped defaults rather than eyeballed in a browser.
//
// Everything is a multiple of S = config.SYMBOL_SIZE (default 155). Nothing here
// may be a pixel value: S is user-mutable through canvas preferences and
// auto-spacing, and the whole diagram is zoom-scaled.
//
// The clearances that matter, all measured from the node centre:
//   symbol           square half-side 0.5S, circle radius 0.564S, diamond 0.707S
//   carrier perimeter a 0.2S stroke ON the symbol edge
//   deceased slash    to 0.62S
//   adoption bracket  to 0.8S
//   cascade badge     centre (-0.52S, -0.52S) r 0.3S, so 1.035S along the diagonal
//   linked-record badge centre -0.85S, nudged to -1.25S, side 0.4S
//   proxy "P"         y -0.7S; nomenclature number x >= 0.95S, y -0.95S or -1.15S
//   pinned dot        (-0.6S, +0.6S); records-reviewed asterisk (+0.65S, +0.65S)
//   fertility bars    0.85S and 1.05S BELOW
//   first label row   BASELINE at 1.5S, drawn at font_size (default 0.45S) with no
//                     dominant-baseline, so its glyph tops sit near 1.18S
//
// That last line is why the tag goes ABOVE the node. Below it there is roughly
// 0.3S of clear space at the default font, which a 0.34S tag does not fit, and a
// tag that renders only on charts with an unusually small font is not a channel.

const SQRT2_OVER_2 = Math.SQRT2 / 2;
const INV_SQRT_PI = 1 / Math.sqrt(Math.PI);

/** Ring radius, stroke and the tag baseline for one slot. */
export function ringGeometry(symbolSize, { isRound = false, slot = 0 } = {}) {
  const S = symbolSize;
  // Mirrors highlightCanvasNodes (clinicalInsightsUi.jsx:33-41): enclose the
  // square's DIAGONAL, not its half-side, or the ring cuts the corners.
  const enclosing = isRound ? S * INV_SQRT_PI : S * SQRT2_OVER_2;
  const dim = isRound ? 2 * enclosing : S;
  const rA = enclosing + 0.34 * dim;
  const radius = slot === 0 ? rA : rA + 0.22 * S;
  const strokeWidth = slot === 0 ? 0.12 * S : 0.1 * S;
  return {
    radius,
    strokeWidth,
    outerEdge: radius + strokeWidth / 2,
    dashArray: slot === 0 ? null : `${0.16 * S} ${0.1 * S}`,
    // Both slots share one tag line, anchored off slot A so two tags align.
    tagY: -(rA + 0.6 * S)
  };
}

/** Ring stroke width for a severity band, when the preset declares one. */
export function weightedStroke(symbolSize, band) {
  const S = symbolSize;
  switch (band) {
    case 'oneWeek':
      return 0.13 * S;
    case 'twoWeeks':
    case 'over2w':
      return 0.16 * S;
    case 'due':
    default:
      return 0.12 * S;
  }
}

/**
 * Tag box for one slot.
 *
 * Slot A's right edge sits at x = -0.12S and the box grows leftward; slot B's
 * left edge sits at +0.12S and grows rightward. That keeps both clear of the
 * vertical parent-to-child descent line at x = 0, which every child node has
 * arriving at its top, and stops the two tags touching.
 */
export function tagGeometry(
  symbolSize,
  { slot = 0, text = '', isRound = false } = {}
) {
  const S = symbolSize;
  const { tagY } = ringGeometry(S, { isRound, slot: 0 });
  const fontSize = 0.2 * S;
  const height = 0.34 * S;
  // 0.62 of the font size is a conservative average glyph advance for the bold
  // uppercase text these tags carry; auto-spacing uses the same style of
  // estimate. The real width is measured after append and the rect corrected.
  const estimated = Math.max(0.3 * S, text.length * fontSize * 0.62 + 0.2 * S);
  const maxWidth = 0.88 * S;
  const width = Math.min(estimated, maxWidth);
  const gap = 0.12 * S;
  const x = slot === 0 ? -gap - width : gap;
  return {
    x,
    y: tagY - height / 2,
    width,
    height,
    rx: 0.17 * S,
    fontSize,
    textX: slot === 0 ? -gap - width / 2 : gap + width / 2,
    textY: tagY,
    strokeWidth: 3
  };
}

/** Everything a paint pass needs for one node, one slot. */
export function markGeometry(symbolSize, { isRound, slot, text, band }) {
  const ring = ringGeometry(symbolSize, { isRound, slot });
  return {
    ring: {
      ...ring,
      strokeWidth: band ? weightedStroke(symbolSize, band) : ring.strokeWidth
    },
    tag: tagGeometry(symbolSize, { slot, text, isRound })
  };
}

/** The label baseline the ring must stay clear of, so tests can assert it. */
export const firstLabelBaseline = (symbolSize) => 1.5 * symbolSize;
